import type { FlagValue } from '@anchorage/feature-flags';
import { Separator } from '@latitude/separator';
import { Text } from '@latitude/text';
import React from 'react';

import { inferType } from '../utils/flagUtils';
import { BooleanFlagInput } from './BooleanFlagInput';
import { ObjectFlagInput } from './ObjectFlagInput';
import { StringNumberFlagInput } from './StringNumberFlagInput';

type FlagItemProps = {
  flagKey: string;
  currentValue: FlagValue;
  displayValue: FlagValue;
  isOverridden: boolean;
  defaultValue: FlagValue | undefined;
  hasUnsavedChanges: boolean;
  onChange: (key: string, value: FlagValue) => void;
  onReset: (key: string) => void;
  onSave: (key: string) => void;
  onBooleanToggle: (key: string, value: boolean) => void;
};

export const FlagItem: React.FC<FlagItemProps> = ({
  flagKey,
  currentValue,
  displayValue,
  isOverridden,
  defaultValue,
  hasUnsavedChanges,
  onChange,
  onReset,
  onSave,
  onBooleanToggle,
}) => {
  const type = inferType(currentValue);
  const hasChanges = isOverridden && defaultValue !== undefined;

  const renderInput = () => {
    switch (type) {
      case 'boolean':
        return (
          <BooleanFlagInput
            flagKey={flagKey}
            value={displayValue as boolean}
            onToggle={onBooleanToggle}
          />
        );

      case 'string':
      case 'number':
        return (
          <StringNumberFlagInput
            flagKey={flagKey}
            type={type}
            displayValue={displayValue}
            isOverridden={isOverridden}
            defaultValue={defaultValue}
            hasUnsavedChanges={hasUnsavedChanges}
            onChange={onChange}
            onReset={onReset}
            onSave={onSave}
          />
        );

      case 'object':
        return (
          <ObjectFlagInput
            flagKey={flagKey}
            displayValue={displayValue}
            isOverridden={isOverridden}
            defaultValue={defaultValue}
            hasUnsavedChanges={hasUnsavedChanges}
            onChange={onChange}
            onReset={onReset}
            onSave={onSave}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <Text variant="bodyMedium">{flagKey}</Text>
          <Text variant="captionRegular" color="muted">
            {type !== 'boolean' && <>Type: {type}</>}
            {hasChanges && (
              <>{type === 'boolean' && <>Default: {String(defaultValue)}</>}</>
            )}
          </Text>
        </div>
        {type === 'boolean' && <div>{renderInput()}</div>}
      </div>
      {type !== 'boolean' && <div>{renderInput()}</div>}
      <Separator />
    </div>
  );
};
